import { Button, Card, Checkbox, Input, InputError } from '@features/ui';
import { FC, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { UpdateUserReq, UserProfile } from '@src/types/users.ts';
import { updateUser } from '@src/services/users.ts';
import { TOption } from '@src/types';
import { useAuthStore } from '@src/stores/authStore';
import { toast } from 'react-toastify';
import { canAccess } from '@src/utils/auth';
import { UserRolePermission } from '@src/types/settings';

interface UsersFormProps {
  user: UserProfile | null;
  countryOptions: TOption[];
  stateOptions: TOption[];
}

const validationSchema = yup.object({
  username: yup.string().required('Username is required'),
  phoneNumber: yup.string().optional(),
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  email: yup
    .string()
    .email('Invalid email format')
    .required('Email is required'),
  isTrading: yup.boolean(),
  companyName: yup.string().when('isTrading', {
    is: true,
    then: (schema) =>
      schema.required('Company name is required when trading as company'),
    otherwise: (schema) => schema.optional(),
  }),
  street: yup.string().optional(),
  city: yup.string().optional(),
  state: yup.string().optional(),
  country: yup.string().optional(),
});

type FormData = yup.InferType<typeof validationSchema>;

const getOptionId = (raw: string, options: TOption[]) => {
  const input = (raw ?? '').trim();

  if (!input) return '';

  // First try to find by exact value match
  const byValue = options.find((o) => o.value === input);

  if (byValue) return byValue.value;

  // Then try to find by exact label match (case insensitive)
  const lower = input.toLowerCase();
  const byLabel = options.find((o) => {
    const optionLabel = (o.label || '').trim().toLowerCase();

    return optionLabel === lower;
  });

  if (byLabel) {
    return byLabel.value;
  }

  // If no exact match, try partial match
  const partialMatch = options.find((o) => {
    const optionLabel = (o.label || '').trim().toLowerCase();

    return optionLabel.includes(lower) || lower.includes(optionLabel);
  });

  if (partialMatch) {
    return partialMatch.value;
  }

  return '';
};

export const TradingUserForm: FC<UsersFormProps> = ({
  user,
  countryOptions,
  stateOptions,
}) => {
  const { auth } = useAuthStore();
  const isAdmin = useMemo(
    () => auth?.user?.role === 'admin',
    [auth?.user?.role]
  );

  const {
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, dirtyFields },
  } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      username: '',
      phoneNumber: '',
      firstName: '',
      lastName: '',
      email: '',
      isTrading: false,
      companyName: '',
      street: '',
      city: '',
      state: '',
      country: '',
    },
  });

  const isTrading = watch('isTrading');
  const watchedValues = watch();

  // Separate state for address input to allow typing commas
  const [addressInput, setAddressInput] = useState('');
  const [addressInputDirty, setAddressInputDirty] = useState(false);

  // Separate state for name input to allow deleting whitespace
  const [nameInput, setNameInput] = useState('');
  const [nameInputDirty, setNameInputDirty] = useState(false);

  const hasChanges =
    Object.keys(dirtyFields).length > 0 || addressInputDirty || nameInputDirty;

  useEffect(() => {
    if (user) {
      const name = user?.company_name ?? '';
      const uname = user?.username ?? '';
      const phone = user?.phone_number ?? '';
      const fname = user?.first_name ?? '';
      const lname = user?.last_name ?? '';
      const mail = user?.user?.email ?? '';
      const trading = Boolean(name);
      const street = user?.street ?? '';
      const city = user?.city ?? '';
      const state =
        stateOptions.find((item) => item.value === user?.state)?.label ?? '';
      const country =
        countryOptions.find((item) => item.value === user?.country)?.label ??
        '';

      // Initialize address input
      const addressParts = [street, city, state, country].filter(Boolean);

      setAddressInput(addressParts.join(', '));
      setAddressInputDirty(false); // Reset dirty state when loading user data

      // Initialize name input
      const fullName = `${fname} ${lname}`.trim();

      setNameInput(fullName);
      setNameInputDirty(false); // Reset name input dirty state

      reset({
        username: uname,
        phoneNumber: phone,
        firstName: fname,
        lastName: lname,
        email: mail,
        isTrading: trading,
        companyName: name,
        street,
        city,
        state,
        country,
      });
    }
  }, [user, reset, stateOptions, countryOptions]);

  const onSubmit = async (data: FormData) => {
    if (!user?.id) return;

    const payload: UpdateUserReq = {};

    if (data.isTrading) {
      payload.is_company = true;
      payload.company_name = data.companyName;
    } else {
      payload.is_company = false;
      payload.company_name = '';
    }

    const stateId = getOptionId(data.state || '', stateOptions);
    const countryId = getOptionId(data.country || '', countryOptions);

    if (data.country && data.country.trim() && !countryId) {
      console.warn('Could not find country:', data.country);
    }

    if (data.state && data.state.trim() && !stateId && countryId === 'US') {
      console.warn('Could not find state:', data.state);
    }
    payload.first_name = data.firstName || '';
    payload.last_name = data.lastName || '';
    payload.street = data.street || '';
    payload.city = data.city || '';
    payload.state = user?.state ? stateId || data.state || '' : '';
    payload.country = countryId || data.country || '';
    payload.phone_number = data.phoneNumber || '';
    payload.username = data.username || '';
    payload.email = data.email || '';

    try {
      await updateUser(user.id, payload);
      reset(data);
      setAddressInputDirty(false);
      setNameInputDirty(false); // Reset name input dirty state
      toast.success('User updated successfully.');
    } catch (error) {
      toast.error('Error updating user. Please try again.');
      console.error('Error updating user:', error);
    }
  };

  const isEnableSaveButton = () => {
    return (
      hasChanges &&
      canAccess(UserRolePermission.EDIT_TRADER_PROFILE, auth?.user?.permissions)
    );
  };

  const isReadOnlyFields =
    !isAdmin ||
    !canAccess(UserRolePermission.EDIT_TRADER_PROFILE, auth?.user?.permissions);

  return (
    <Card className={'flex-1 pl-6.5 pr-12 pt-[31px] pb-[37px]'}>
      <div className={'flex gap-5'}>
        <div className="flex-1">
          <Input
            error={!!errors.username}
            readOnly={true}
            textSize={'xs'}
            value={watchedValues.username || ''}
            variant={'secondary'}
            onChange={(e) =>
              setValue('username', e.target.value, { shouldDirty: true })
            }
          />
          {errors.username && (
            <InputError>{errors.username.message}</InputError>
          )}
        </div>
        <div className="flex-1">
          <Input
            readOnly={true}
            textSize={'xs'}
            value={watchedValues.phoneNumber || ''}
            variant={'secondary'}
            onChange={(e) =>
              setValue('phoneNumber', e.target.value, { shouldDirty: true })
            }
          />
        </div>
      </div>
      <div className={'flex gap-5 mt-9 mb-7'}>
        <div className="flex-1">
          <Input
            error={!!errors.firstName || !!errors.lastName}
            readOnly={isReadOnlyFields}
            textSize={'xs'}
            value={nameInput}
            variant={'secondary'}
            onBlur={() => {
              // Parse the name when user finishes typing (on blur)
              const [firstName, ...lastNameParts] = nameInput.split(' ');

              setValue('firstName', firstName || '', { shouldDirty: true });
              setValue('lastName', lastNameParts.join(' ') || '', {
                shouldDirty: true,
              });
            }}
            onChange={(e) => {
              setNameInput(e.target.value);
              setNameInputDirty(true); // Mark as dirty when user types
            }}
          />
          {(errors.firstName || errors.lastName) && (
            <InputError>
              {errors.firstName?.message || errors.lastName?.message}
            </InputError>
          )}
        </div>
        <div className="flex-1">
          <Input
            error={!!errors.email}
            readOnly={isReadOnlyFields}
            textSize={'xs'}
            value={watchedValues.email || ''}
            variant={'secondary'}
            onChange={(e) =>
              setValue('email', e.target.value, { shouldDirty: true })
            }
          />
          {errors.email && <InputError>{errors.email.message}</InputError>}
        </div>
      </div>
      <div className={'flex gap-5 items-center'}>
        <Checkbox
          checked={isTrading}
          className={'flex-1'}
          disabled={
            !canAccess(
              UserRolePermission.EDIT_TRADES_AS_COMPANY,
              auth?.user?.permissions
            )
          }
          label={'Trading as a company?'}
          onCheckedChange={(val) =>
            setValue('isTrading', Boolean(val), { shouldDirty: true })
          }
        />
        {isTrading && (
          <div className={'flex-1 flex gap-2 items-center'}>
            <div className="flex-1">
              <Input
                error={!!errors.companyName}
                placeholder={'Company name'}
                readOnly={isReadOnlyFields}
                textSize={'xs'}
                value={watchedValues.companyName || ''}
                variant={'secondary'}
                onChange={(e) =>
                  setValue('companyName', e.target.value, { shouldDirty: true })
                }
              />
              {errors.companyName && (
                <InputError>{errors.companyName.message}</InputError>
              )}
            </div>
          </div>
        )}
      </div>
      <div>
        <Input
          className={'mt-7'}
          readOnly={isReadOnlyFields}
          textSize={'xs'}
          value={addressInput}
          variant={'secondary'}
          onBlur={() => {
            const [street = '', city = '', stateLabel = '', countryLabel = ''] =
              addressInput.split(',').map((s) => s.trim());

            setValue('street', street, { shouldDirty: true });
            setValue('city', city, { shouldDirty: true });
            if (user?.state) {
              setValue('state', stateLabel, { shouldDirty: true });
              setValue('country', countryLabel, { shouldDirty: true });
            } else {
              setValue('country', stateLabel, { shouldDirty: true });
            }
          }}
          onChange={(e) => {
            setAddressInput(e.target.value);
            setAddressInputDirty(true);
          }}
        />
        {(errors.country || errors.state || errors.city || errors.street) && (
          <InputError>
            {errors.country?.message ||
              errors.state?.message ||
              errors.city?.message ||
              errors.street?.message}
          </InputError>
        )}
      </div>

      {isEnableSaveButton() && (
        <Button
          className="w-fit mt-7"
          size={'sm'}
          variant={'lime'}
          onClick={(e) => {
            e.preventDefault();
            handleSubmit(onSubmit)(e);
          }}
        >
          Save
        </Button>
      )}
    </Card>
  );
};
